import {
	type OpenInvoice,
	OpenInvoiceListInput,
	type OpenInvoiceListQuery,
	OpenInvoiceState,
} from "@cleanbudget/contract/invoice";
import { Button } from "@cleanbudget/ui/components/button";
import { useToast } from "@cleanbudget/ui/components/toast";
import { IconLoader2, IconMail } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { HelpButton } from "@/components/help/help-button";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { apiErrorTag } from "@/lib/api-error";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { client, orpc } from "@/lib/orpc";

const openInvoicesQuery = (query: OpenInvoiceListQuery) => orpc.invoice.open.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/order/invoices` is the default view. */
const { sort, order, page, pageSize } = OpenInvoiceListInput.parse({});

/**
 * Open invoices (#98): the on-account invoices not paid yet, the nearest due date first, the
 * overdue ones marked with how late they are. One click mails the customer a reminder; it is
 * logged in the order's history and the mail log. Nothing goes automatically. On a phone: invoice
 * and customer, with the due date under the invoice, and amount and button under the customer.
 */
export const Route = createFileRoute("/_admin/order/invoices")({
	validateSearch: OpenInvoiceListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(openInvoicesQuery(deps)),
	component: OpenInvoicesPage,
});

function OpenInvoicesPage() {
	const { t } = useTranslation("invoice");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(openInvoicesQuery(search));

	const columns: readonly Column<OpenInvoice>[] = [
		{
			id: "number",
			header: t("column.number"),
			sortable: true,
			cell: (item) => (
				<span className="flex flex-col gap-1">
					<span className="font-medium tabular-nums">{item.number}</span>
					<Link
						to="/order/$orderId"
						params={{ orderId: item.orderId }}
						className="text-xs text-muted-foreground tabular-nums hover:underline"
					>
						{t("order", { orderNumber: item.orderNumber })}
					</Link>
					<span className="md:hidden">
						<DueDate item={item} />
					</span>
				</span>
			),
		},
		{
			id: "customer",
			header: t("column.customer"),
			sortable: true,
			cell: (item) => (
				<span className="flex min-w-0 flex-col gap-1 whitespace-normal">
					<Link
						to="/customer/$customerId"
						params={{ customerId: item.customerId }}
						className="font-medium break-words hover:underline"
					>
						{item.customerName}
					</Link>
					<span className="text-xs break-all text-muted-foreground">{item.email}</span>
					<span className="tabular-nums md:hidden">{formatCents(item.totalIncl)}</span>
					<span className="pt-1 md:hidden">
						<RemindButton item={item} />
					</span>
				</span>
			),
		},
		{
			id: "total",
			header: t("column.total"),
			sortable: true,
			secondary: true,
			align: "right",
			cell: (item) => <span className="tabular-nums">{formatCents(item.totalIncl)}</span>,
		},
		{
			id: "dueOn",
			header: t("column.dueOn"),
			sortable: true,
			secondary: true,
			cell: (item) => <DueDate item={item} />,
		},
		{
			id: "reminder",
			header: (
				<span className="inline-flex items-center gap-1">
					{t("column.reminder")}
					<HelpButton topic="invoice:reminder" />
				</span>
			),
			secondary: true,
			cell: (item) => (
				<span className="flex flex-col items-start gap-1.5">
					<span className="text-xs text-muted-foreground">
						{item.lastReminderAt === null
							? t("noReminder")
							: t("reminders", { count: item.reminders, date: formatDateTime(item.lastReminderAt) })}
					</span>
					<RemindButton item={item} />
				</span>
			),
		},
	];

	const filtered = Boolean(search.search) || search.state !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/order">{t("backToOrders")}</BackLink>}
				title={t("title")}
				help="invoice:openInvoices"
				description={t("description")}
			/>
			<p className="text-sm" role="status">
				{data.overdueCount === 0
					? t("summary.noneOverdue")
					: t("summary.overdue", { count: data.overdueCount, amount: formatCents(data.overdueTotal) })}
			</p>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="state"
						label={t("state.label")}
						value={search.state}
						options={OpenInvoiceState.options.map((state) => ({
							value: state,
							label: t(`state.${state}`),
						}))}
						help="invoice:dueState"
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(item) => item.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}

/** The due date, and how late it is once overdue. */
function DueDate({ item }: { item: OpenInvoice }) {
	const { t } = useTranslation("invoice");
	return (
		<span className="flex flex-wrap items-center gap-2">
			<time dateTime={item.dueOn} className="tabular-nums">
				{formatCalendarDate(item.dueOn)}
			</time>
			{item.state === "overdue" ? (
				<StatusBadge tone="negative">{t("daysOverdue", { count: item.daysOverdue })}</StatusBadge>
			) : (
				<StatusBadge tone="neutral">{t("dueIn")}</StatusBadge>
			)}
		</span>
	);
}

/**
 * One click mails the reminder; a toast says it went (or why not), the list shows the count. When
 * one went out today already (the API says so) the click asks first, and sends `again`.
 */
function RemindButton({ item }: { item: OpenInvoice }) {
	const { t } = useTranslation("invoice");
	const toast = useToast();
	const queryClient = useQueryClient();
	const sent = () => {
		toast.add({ title: t("reminded", { number: item.number, email: item.email }) });
		return queryClient.invalidateQueries({ queryKey: orpc.invoice.open.key() });
	};
	const remind = useMutation(
		orpc.invoice.remind.mutationOptions({
			onSuccess: sent,
			onError: (error) => {
				toast.add({
					title: match(apiErrorTag(error))
						.with("InvoiceNotOpen", () => t("notOpen"))
						.with("ReminderSentToday", () => t("remindedToday"))
						.otherwise(() => t("remindFailed")),
				});
				return queryClient.invalidateQueries({ queryKey: orpc.invoice.open.key() });
			},
		}),
	);
	const button = (onClick?: () => void) => (
		<Button
			variant="outline"
			size="sm"
			disabled={remind.isPending}
			aria-label={t("remindLabel", { number: item.number })}
			onClick={onClick}
		>
			{remind.isPending ? <IconLoader2 className="animate-spin" aria-hidden /> : <IconMail aria-hidden />}
			{t("remind")}
		</Button>
	);
	if (item.remindedToday && item.lastReminderAt !== null) {
		return (
			<ConfirmDialog
				trigger={button()}
				destructive={false}
				title={t("againTitle", { number: item.number })}
				description={t("againDescription", { date: formatDateTime(item.lastReminderAt), email: item.email })}
				confirmLabel={t("againConfirm")}
				onConfirm={async () => {
					// Sent although one went out today: the employee confirmed.
					await client.invoice.remind({ invoiceId: item.id, again: true });
					await sent();
				}}
				errorMessage={(error) =>
					match(apiErrorTag(error))
						.with("InvoiceNotOpen", () => t("notOpen"))
						.otherwise(() => t("remindFailed"))
				}
			/>
		);
	}
	return button(() => remind.mutate({ invoiceId: item.id }));
}
